// src/hooks/useServices.ts
import { useState, useMemo } from 'react';
import { services, Service } from '../data/services';

export const useServices = () => {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'price' | 'name' | 'popular'>('popular');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = ['all', ...Array.from(new Set(services.map(service => service.category)))];

  const filteredServices = useMemo(() => {
    let result = services;

    // Filter by category
    if (activeCategory !== 'all') {
      result = result.filter(service => service.category === activeCategory);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(service =>
        service.name.toLowerCase().includes(query) ||
        service.description.toLowerCase().includes(query) ||
        service.features.some(feature => feature.toLowerCase().includes(query))
      );
    }

    // Sort results
    switch (sortBy) {
      case 'price':
        return [...result].sort((a, b) => a.price - b.price);
      case 'name':
        return [...result].sort((a, b) => a.name.localeCompare(b.name));
      case 'popular':
        return [...result].sort((a, b) => {
          if (a.popular && !b.popular) return -1;
          if (!a.popular && b.popular) return 1;
          return 0;
        });
      default:
        return result;
    }
  }, [activeCategory, sortBy, searchQuery]);

  const getServiceById = (id: string): Service | undefined => {
    return services.find(service => service.id === id);
  };

  const getServicesByCategory = (category: string): Service[] => {
    return services.filter(service => service.category === category);
  };

  const getPopularServices = (): Service[] => {
    return services.filter(service => service.popular);
  };

  return {
    allServices: services,
    filteredServices,
    categories,
    activeCategory,
    sortBy,
    searchQuery,
    setActiveCategory,
    setSortBy,
    setSearchQuery,
    getServiceById,
    getServicesByCategory,
    getPopularServices,
  };
};